import { useLocalization } from '@lib/localization';
import { Box, Divider, MenuItem, Select } from '@mui/material';
import { defaultMarkStyles, pendingDotStyles, selectStyles } from '@pages/AppSettings.styles';
import { ownerOptionStyles } from '@pages/ReportDesigns.styles';
import { BuildingIcon, UserFriendsIcon } from 'react-line-awesome';

/** What the picker calls the organization, among its teams' ids. Also what `?scope=` holds for it. */
export const ORGANIZATION = 'organization';

interface Props {
  /** A team's id, or `ORGANIZATION`. */
  value: string;
  teams: { _id: string; name: string }[];
  /** The reader's default team: the one whose settings their devices take and whose brand their reports carry. */
  defaultTeamId: string | undefined;
  /** Whether a scope holds changes that are not saved yet. */
  hasUnsaved: (scope: string) => boolean;
  onChange: (scope: string) => void;
}

/**
 * Whose layer is on screen: the organization's, which every team's sits on, or one team's.
 * The picker of the two pages that edit such layers, the app settings and the report brand.
 * It marks the reader's default team: among the teams they may open, that is the one whose
 * settings their own devices take and whose brand their own reports carry.
 */
const ScopePicker: React.FC<Props> = ({ value, teams, defaultTeamId, hasUnsaved, onChange }) => {
  const { t } = useLocalization();

  return (
    <Select
      size="small"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      sx={{ ...selectStyles, minWidth: 240 }}
    >
      {/* First, and apart from the teams: it is what each of them starts from. */}
      <MenuItem value={ORGANIZATION} sx={ownerOptionStyles}>
        <BuildingIcon /> {t('AppSettings_Organization')}
        {hasUnsaved(ORGANIZATION) && <Box component="span" sx={pendingDotStyles} />}
      </MenuItem>
      {teams.length > 0 && <Divider />}
      {teams.map((team) => (
        <MenuItem key={team._id} value={team._id} sx={ownerOptionStyles}>
          <UserFriendsIcon /> {team.name}
          {team._id === defaultTeamId && <Box component="span" sx={defaultMarkStyles}>{t('Settings_DefaultTeam')}</Box>}
          {hasUnsaved(team._id) && <Box component="span" sx={pendingDotStyles} />}
        </MenuItem>
      ))}
    </Select>
  );
};

export default ScopePicker;
